import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import BadgeFlussoCommerciale from '../../../components/BadgeFlussoCommerciale';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { API_DOCS } from './PreventiviPage.api';
import './PreventiviPage.css';

// ── Icone ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoPreventivo = 'SEMPLICE' | 'CONFIGURATO_COMMESSA';
type FlussoCommerciale = 'VENDITA' | 'APPALTO_CONTRATTO';
type StatoPreventivo =
  | 'BOZZA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO'
  | 'SCADUTO' | 'SUPERATO' | 'CONVERTITO' | 'ANNULLATO'
  | 'ANNULLATO_DOPO_CONFERMA';

interface PreventivoItem {
  id: number;
  numero: string;
  versione: number;
  tipo: TipoPreventivo;
  flussoCommerciale: FlussoCommerciale | null;
  stato: StatoPreventivo;
  oggetto: string;
  dataEmissione: string;
  dataValidita: string | null;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  totaleCliente: string | null;
  totaleImponibileCliente: string | null;
  commessaId: number | null;
  commessa: { id: number; numero: string } | null;
  acquirente: { id: number; nome: string; cognome: string; unitaCodice: string } | null;
  offertaCapitolatoId: number | null;
  sedeId: number | null;
}

interface SedeOption { id: number; nome: string; societaId?: number }
interface CodiceIvaOption { id: number; codice: string; descrizione: string; aliquota: string; attivo: boolean; predefinito?: boolean }
interface NominativoOption {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
  cognome: string | null;
  codice: string;
  tipoSoggetto?: 'AZIENDA' | 'PRIVATO';
  isCliente?: boolean;
}
interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
  societaId: number;
  offertaCapitolatoId?: number | null;
  sedeId?: number | null;
  sedeNome?: string | null;
  codiceIvaDefaultId?: number | null;
  codiceIvaDefault?: { id: number; codice: string; descrizione: string; aliquota: string } | null;
}
interface AcquirenteOption { id: number; unitaCodice: string; unitaDescrizione: string | null; nome: string | null; cognome: string | null }
interface OffertaCapitolatoLite { id: number; codice: string; ricarico: string | null; tipoFatturazione: string | null }

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoPreventivo, string> = {
  BOZZA: 'Bozza',
  INVIATO: 'Inviato',
  ACCETTATO: 'Accettato',
  RIFIUTATO: 'Rifiutato',
  SCADUTO: 'Scaduto',
  SUPERATO: 'Superato',
  CONVERTITO: 'Convertito',
  ANNULLATO: 'Annullato',
  ANNULLATO_DOPO_CONFERMA: 'Annullato dopo conferma',
};

const STATO_CLS: Record<StatoPreventivo, string> = {
  BOZZA: 'pre-badge-stato--bozza',
  INVIATO: 'pre-badge-stato--inviato',
  ACCETTATO: 'pre-badge-stato--accettato',
  RIFIUTATO: 'pre-badge-stato--rifiutato',
  SCADUTO: 'pre-badge-stato--scaduto',
  SUPERATO: 'pre-badge-stato--superato',
  CONVERTITO: 'pre-badge-stato--convertito',
  ANNULLATO: 'pre-badge-stato--annullato',
  ANNULLATO_DOPO_CONFERMA: 'pre-badge-stato--annullato-conferma',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function renderCliente(p: PreventivoItem): React.ReactNode {
  if (p.acquirente) {
    const nome = `${p.acquirente.nome || ''} ${p.acquirente.cognome || ''}`.trim();
    return <span>{nome || '—'} <span className="bop-text-muted">• {p.acquirente.unitaCodice}</span></span>;
  }
  if (p.clienteRagioneSociale) return p.clienteRagioneSociale;
  const nomeCog = `${p.clienteNome || ''} ${p.clienteCognome || ''}`.trim();
  return nomeCog || <span className="bop-text-muted">—</span>;
}

function nominativoLabel(n: NominativoOption): string {
  if (n.tipoSoggetto === 'AZIENDA' && n.ragioneSociale) return n.ragioneSociale;
  if (n.nome || n.cognome) return `${n.nome || ''} ${n.cognome || ''}`.trim();
  return n.ragioneSociale || n.codice || `Nominativo #${n.id}`;
}

// Valore stringa "cliente" per sort/filtro colonna Cliente (allineato al render).
function clienteText(p: PreventivoItem): string {
  if (p.acquirente) {
    const nome = `${p.acquirente.nome || ''} ${p.acquirente.cognome || ''}`.trim();
    return `${nome} ${p.acquirente.unitaCodice}`.trim();
  }
  if (p.clienteRagioneSociale) return p.clienteRagioneSociale;
  return `${p.clienteNome || ''} ${p.clienteCognome || ''}`.trim();
}

// Definizione colonne tabella Preventivi (estratta per leggibilità).
function buildPreventiviColumns(): BopExcelColumn<PreventivoItem>[] {
  return [
    {
      key: 'numero',
      header: 'Numero',
      accessor: (p) => p.numero,
      filter: { type: 'text' },
      width: '130px',
      render: (p) => (
        <>
          <span className="pre-numero-link">{p.numero}</span>
          {p.versione > 1 && (
            <span className="pre-versione-badge" title={`Versione ${p.versione}`}>v{p.versione}</span>
          )}
        </>
      ),
    },
    {
      key: 'dataEmissione',
      header: 'Data',
      accessor: (p) => p.dataEmissione || '',
      filter: { type: 'dateRange' },
      width: '110px',
      render: (p) => formatDateIT(p.dataEmissione),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      accessor: (p) => p.tipo,
      filter: {
        type: 'enum',
        options: [
          { value: 'SEMPLICE',            label: 'Semplice' },
          { value: 'CONFIGURATO_COMMESSA', label: 'Capitolato commessa' },
        ],
      },
      width: '160px',
      render: (p) => (
        <span className={`pre-tipo-pill ${p.tipo === 'SEMPLICE' ? 'pre-tipo-pill--semplice' : 'pre-tipo-pill--configurato'}`}>
          {p.tipo === 'SEMPLICE' ? 'Semplice' : 'Capitolato commessa'}
        </span>
      ),
    },
    {
      key: 'flussoCommerciale',
      header: 'Flusso',
      accessor: (p) => p.flussoCommerciale || '',
      filter: {
        type: 'enum',
        options: [
          { value: 'VENDITA',           label: 'Vendita' },
          { value: 'APPALTO_CONTRATTO', label: 'Appalto / Contratto' },
        ],
      },
      width: '130px',
      render: (p) => <BadgeFlussoCommerciale flusso={p.flussoCommerciale} compatto />,
    },
    {
      key: 'commessa',
      header: 'Commessa',
      accessor: (p) => p.commessa?.numero || '',
      filter: { type: 'text' },
      width: '120px',
      stopRowClick: true,
      render: (p) => p.commessa ? (
        <Link className="pre-commessa-link" to={`/commerciale/commesse/${p.commessa.id}`}>
          {p.commessa.numero}
        </Link>
      ) : (
        <span className="bop-text-muted">—</span>
      ),
    },
    {
      key: 'cliente',
      header: 'Cliente / Acquirente',
      accessor: clienteText,
      filter: { type: 'text' },
      render: (p) => renderCliente(p),
    },
    {
      key: 'oggetto',
      header: 'Oggetto',
      accessor: (p) => p.oggetto,
      filter: { type: 'text' },
      render: (p) => p.oggetto,
    },
    {
      key: 'totaleCliente',
      header: 'Totale',
      accessor: (p) => p.totaleCliente != null ? Number(p.totaleCliente) : null,
      filter: { type: 'number' },
      width: '120px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right pre-totale',
      render: (p) => p.totaleCliente != null
        ? formatCurrency(Number(p.totaleCliente))
        : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (p) => p.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'BOZZA',      label: 'Bozza' },
          { value: 'INVIATO',    label: 'Inviato' },
          { value: 'ACCETTATO',  label: 'Accettato' },
          { value: 'RIFIUTATO',  label: 'Rifiutato' },
          { value: 'SCADUTO',    label: 'Scaduto' },
          { value: 'SUPERATO',   label: 'Superato' },
          { value: 'CONVERTITO', label: 'Convertito' },
          { value: 'ANNULLATO',  label: 'Annullato' },
          { value: 'ANNULLATO_DOPO_CONFERMA', label: 'Annullato dopo conferma' },
        ],
      },
      width: '120px',
      render: (p) => (
        <span className={`pre-badge-stato ${STATO_CLS[p.stato]}`}>
          {STATO_LABEL[p.stato]}
        </span>
      ),
    },
  ];
}

// ── Modale Nuovo Preventivo ───────────────────────────────────────────────────

export function ModalNuovoPreventivo({
  societaId,
  onClose,
  onCreato,
  initialTipo,
  initialCommessa,
}: {
  societaId: string;
  onClose: () => void;
  onCreato: (id: number) => void;
  initialTipo?: 'SEMPLICE' | 'CONFIGURATO_COMMESSA';
  initialCommessa?: { id: number; numero: string; descrizione?: string };
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [flussoCommerciale, setFlussoCommerciale] = useState<FlussoCommerciale | ''>('');
  const [tipo, setTipo] = useState<TipoPreventivo | ''>('');

  // Dati comuni
  const [sedi, setSedi] = useState<SedeOption[]>([]);
  const [codiciIva, setCodiciIva] = useState<CodiceIvaOption[]>([]);
  const [acquirenti, setAcquirenti] = useState<AcquirenteOption[]>([]);
  const [offertaCollegata, setOffertaCollegata] = useState<OffertaCapitolatoLite | null>(null);

  // Form state
  const [sedeId, setSedeId] = useState('');
  const [oggetto, setOggetto] = useState('');
  // True quando l'utente digita manualmente l'oggetto: blocca l'auto-compilazione
  const [oggettoModificato, setOggettoModificato] = useState(false);
  const [dataEmissione, setDataEmissione] = useState(() => new Date().toISOString().slice(0, 10));
  const [dataValidita, setDataValidita] = useState('');
  const [codiceIvaDefaultId, setCodiceIvaDefaultId] = useState('');
  const [nominativoId, setNominativoId] = useState('');

  // State ricerca cliente (sostituisce la combo)
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteResults, setClienteResults] = useState<NominativoOption[]>([]);
  const [clienteSearching, setClienteSearching] = useState(false);
  const [nominativoSelezionato, setNominativoSelezionato] = useState<NominativoOption | null>(null);

  // State mini-modale nuovo nominativo
  const [miniNewOpen, setMiniNewOpen] = useState(false);
  const [mnTipoSoggetto, setMnTipoSoggetto] = useState<'AZIENDA' | 'PRIVATO'>('PRIVATO');
  const [mnRagioneSociale, setMnRagioneSociale] = useState('');
  const [mnNome, setMnNome] = useState('');
  const [mnCognome, setMnCognome] = useState('');
  const [mnEmail, setMnEmail] = useState('');
  const [mnTelefono, setMnTelefono] = useState('');
  const [mnSaving, setMnSaving] = useState(false);

  // Configurato
  const [modoCommessa, setModoCommessa] = useState<'esistente' | 'nuova'>('esistente');
  const [commessaId, setCommessaId] = useState('');
  const [acquirenteId, setAcquirenteId] = useState('');

  // State ricerca commessa inline
  const [commessaSearchOpen, setCommessaSearchOpen] = useState(false);
  const [commessaSearch, setCommessaSearch] = useState('');
  const [commessaResults, setCommessaResults] = useState<CommessaOption[]>([]);
  const [commessaSearching, setCommessaSearching] = useState(false);
  const [commessaSelezionata, setCommessaSelezionata] = useState<CommessaOption | null>(null);

  // I risultati di ricerca cliente/commessa stanno nel flusso del corpo modale
  // (che scorre): quando cambiano li portiamo in vista, altrimenti finiscono sotto il footer.
  const dropdownClienteRef = useRef<HTMLDivElement>(null);
  const dropdownCommessaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dropdownClienteRef.current?.scrollIntoView({ block: 'nearest' });
  }, [clienteSearchOpen, clienteResults, clienteSearching]);
  useEffect(() => {
    dropdownCommessaRef.current?.scrollIntoView({ block: 'nearest' });
  }, [commessaSearchOpen, commessaResults, commessaSearching]);

  // Per tipo SEMPLICE: se true mostra picker commessa (e nasconde picker cliente).
  // Se false mostra picker cliente classico.
  const [usaCommessaSemplice, setUsaCommessaSemplice] = useState(false);

  const [saving, setSaving] = useState(false);

  // Preselezione da chiamante (es. tab Preventivi di SchedaCommessa)
  useEffect(() => {
    if (initialTipo) {
      // initialTipo viene passato solo da contesti commessa → flusso APPALTO_CONTRATTO
      setFlussoCommerciale('APPALTO_CONTRATTO');
      setTipo(initialTipo);
      setStep(3);
    }
    if (initialCommessa) {
      setCommessaId(String(initialCommessa.id));
      setCommessaSelezionata({
        id: initialCommessa.id,
        numero: initialCommessa.numero,
        descrizione: initialCommessa.descrizione || '',
        clienteRagioneSociale: '',
        societaId: parseInt(societaId, 10),
      } as CommessaOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selezionaTipo = (t: TipoPreventivo) => {
    setTipo(t);
    setUsaCommessaSemplice(false);
    setCommessaId('');
    setCommessaSelezionata(null);
    setNominativoId('');
    setNominativoSelezionato(null);
    setAcquirenteId('');
    setAcquirenti([]);
  };

  // Selezione flusso: VENDITA salta lo step "tipo" e va direttamente al form (step 3)
  // forzando tipo=SEMPLICE; APPALTO_CONTRATTO va a step 2 per scegliere il tipo.
  const selezionaFlusso = (f: FlussoCommerciale) => {
    setFlussoCommerciale(f);
    if (f === 'VENDITA') {
      selezionaTipo('SEMPLICE');
      setStep(3);
    } else {
      setTipo('');
      setStep(2);
    }
  };

  const indietroDaStep3 = () => {
    // Se VENDITA si era saltato step 2 → torna a step 1 e azzera flusso
    if (flussoCommerciale === 'VENDITA') {
      setFlussoCommerciale('');
      setTipo('');
      setStep(1);
    } else {
      setStep(2);
    }
  };

  const indietroDaStep2 = () => {
    setFlussoCommerciale('');
    setTipo('');
    setStep(1);
  };

  // Chiusura modale: chiede conferma se l'utente ha già fatto scelte parziali
  const richiediChiusura = () => {
    const hasPartial = !!flussoCommerciale || !!tipo || step > 1;
    if (hasPartial && !saving) {
      // eslint-disable-next-line no-restricted-globals, no-alert
      if (!window.confirm('Vuoi davvero annullare? Le scelte verranno perse.')) return;
    }
    onClose();
  };

  // Carica sedi + codici IVA + nominativi + commesse alla prima società definita
  useEffect(() => {
    if (!societaId) return;
    // @api GET /api/sedi?societaId={id}&commerciale=true -> {"sedi":[{"id":"number","nome":"string","sedeCommerciale":"boolean"}]}
    apiFetch<{ sedi: SedeOption[] }>(`/api/sedi?societaId=${societaId}&commerciale=true`)
      .then(r => setSedi(r.sedi || []))
      .catch(() => setSedi([]));

    // @api GET /api/codici-iva?societaId={id}&attivo=true -> {"codici":[{"id":"number","codice":"string","descrizione":"string","aliquota":"string","attivo":"boolean","predefinito":"boolean"}]}
    apiFetch<{ codici: CodiceIvaOption[] }>(`/api/codici-iva?societaId=${societaId}&attivo=true`)
      .then(r => {
        const lista = r.codici || [];
        setCodiciIva(lista);
        const pre = lista.find(c => c.predefinito);
        if (pre) setCodiceIvaDefaultId(String(pre.id));
      })
      .catch(() => setCodiciIva([]));
  }, [societaId]);

  // Quando cambia commessa selezionata: carica dettaglio completo (sede/IVA/offerta) + acquirenti
  useEffect(() => {
    if (!commessaId) {
      setOffertaCollegata(null);
      setAcquirenti([]);
      setAcquirenteId('');
      setCommessaSelezionata(null);
      return;
    }
    const cId = parseInt(commessaId, 10);
    if (isNaN(cId)) return;

    // @api GET /api/commesse/:id -> {"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string","sedeId":"number","sedeNome":"string","offertaCapitolatoId":"number|null","codiceIvaDefaultId":"number|null","codiceIvaDefault":{"id":"number","codice":"string","descrizione":"string","aliquota":"string"}}
    apiFetch<CommessaOption & { sedeId: number; sedeNome: string; codiceIvaDefaultId: number | null; codiceIvaDefault: { id: number; codice: string; descrizione: string; aliquota: string } | null }>(`/api/commesse/${cId}`)
      .then(async c => {
        setCommessaSelezionata({
          id: c.id,
          numero: c.numero,
          descrizione: c.descrizione,
          clienteRagioneSociale: c.clienteRagioneSociale,
          societaId: c.societaId,
          offertaCapitolatoId: c.offertaCapitolatoId ?? null,
          sedeId: c.sedeId,
          sedeNome: c.sedeNome,
          codiceIvaDefaultId: c.codiceIvaDefaultId ?? null,
          codiceIvaDefault: c.codiceIvaDefault ?? null,
        });

        // Oggetto di default — solo se non digitato manualmente
        if (!oggettoModificato && !oggetto) {
          setOggetto(`Preventivo per commessa ${c.numero}`);
        }

        if (tipo === 'CONFIGURATO_COMMESSA') {
          // Sede e IVA forzate dalla commessa
          setSedeId(c.sedeId ? String(c.sedeId) : '');
          setCodiceIvaDefaultId(c.codiceIvaDefaultId ? String(c.codiceIvaDefaultId) : '');

          // Offerta collegata se presente
          if (c.offertaCapitolatoId) {
            // @api GET /api/offerte-capitolato/:id -> {"success":true,"data":{"id":"number","codice":"string","ricarico":"string","tipoFatturazione":"string"}}
            const dett = await apiFetch<{ success: boolean; data: OffertaCapitolatoLite }>(
              `/api/offerte-capitolato/${c.offertaCapitolatoId}`
            );
            setOffertaCollegata(dett.data);
          } else {
            setOffertaCollegata(null);
          }
        } else {
          // SEMPLICE + commessa: sede SEMPRE sovrascritta dalla commessa.
          // NON forzare IVA (l'utente sceglie), NON considerare capitolato.
          if (c.sedeId) setSedeId(String(c.sedeId));
          setOffertaCollegata(null);
        }
      })
      .catch(() => {
        setCommessaSelezionata(null);
        setOffertaCollegata(null);
      });

    if (tipo === 'CONFIGURATO_COMMESSA') {
      // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[{"id":"number","unitaCodice":"string","nome":"string","cognome":"string"}]}
      apiFetch<{ success: boolean; data: AcquirenteOption[] }>(`/api/commesse/${cId}/acquirenti`)
        .then(r => setAcquirenti(r.data || []))
        .catch(() => setAcquirenti([]));
    } else {
      setAcquirenti([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commessaId, tipo]);

  // Debounce ricerca cliente
  useEffect(() => {
    if (!clienteSearchOpen || !societaId) return;
    const qSearch = clienteSearch.trim();
    if (qSearch.length < 2) {
      setClienteResults([]);
      return;
    }
    setClienteSearching(true);
    const t = setTimeout(async () => {
      try {
        // @api GET /api/nominativi?ruolo=CLIENTE&societaId={id}&q={q}&limit=20 -> {"data":[{"id":"number","ragioneSociale":"string","nome":"string","cognome":"string","codice":"string","tipoSoggetto":"AZIENDA|PRIVATO"}]}
        const r = await apiFetch<{ data: NominativoOption[] }>(
          `/api/nominativi?ruolo=CLIENTE&societaId=${societaId}&q=${encodeURIComponent(qSearch)}&limit=20`
        );
        setClienteResults(r.data || []);
      } catch {
        setClienteResults([]);
      } finally {
        setClienteSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [clienteSearch, clienteSearchOpen, societaId]);

  const onSelezionaNominativo = (n: NominativoOption) => {
    setNominativoId(String(n.id));
    setNominativoSelezionato(n);
    setClienteSearchOpen(false);
    setClienteSearch('');
    setClienteResults([]);

    // Proposta IVA di default dal cliente selezionato.
    // L'utente può comunque modificarla dopo. Non si applica al flusso
    // CONFIGURATO_COMMESSA, dove l'IVA è forzata dalla commessa.
    if (tipo !== 'CONFIGURATO_COMMESSA') {
      // @api GET /api/nominativi/:id -> {"id":"number","cliente":{"codiceIvaDefaultId":"number|null","codiceIvaDefault":{"id":"number","codice":"string","descrizione":"string"}}}
      apiFetch<{ cliente?: { codiceIvaDefaultId?: number | null } | null }>(`/api/nominativi/${n.id}`)
        .then(det => {
          const ivaId = det?.cliente?.codiceIvaDefaultId;
          if (ivaId) setCodiceIvaDefaultId(String(ivaId));
        })
        .catch(() => { /* in caso di errore si mantiene l'IVA predefinita della società */ });
    }
  };

  const onRimuoviNominativo = () => {
    setNominativoId('');
    setNominativoSelezionato(null);
  };

  // Auto-fill oggetto quando viene selezionato un nominativo —
  // solo se l'utente non ha digitato manualmente l'oggetto
  useEffect(() => {
    if (nominativoSelezionato && !oggettoModificato) {
      setOggetto(`Preventivo ${nominativoLabel(nominativoSelezionato)}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nominativoSelezionato]);

  // Debounce ricerca commessa
  useEffect(() => {
    const ricercaAttiva =
      (tipo === 'CONFIGURATO_COMMESSA') ||
      (tipo === 'SEMPLICE' && usaCommessaSemplice);
    if (!ricercaAttiva || !commessaSearchOpen || !societaId) return;
    const q = commessaSearch.trim();
    setCommessaSearching(true);
    const t = setTimeout(async () => {
      try {
        const qs = `societaId=${societaId}` + (q.length >= 2 ? `&search=${encodeURIComponent(q)}` : '');
        // @api GET /api/commesse?societaId={id}&search={q} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string","offertaCapitolatoId":"number|null","sedeId":"number","sedeNome":"string","codiceIvaDefaultId":"number|null"}]}
        const r = await apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?${qs}`);
        setCommessaResults((r.commesse || []).slice(0, 20));
      } catch {
        setCommessaResults([]);
      } finally {
        setCommessaSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [commessaSearch, commessaSearchOpen, societaId, tipo, usaCommessaSemplice]);

  const onSelezionaCommessa = (c: CommessaOption) => {
    setCommessaId(String(c.id));
    setCommessaSearchOpen(false);
    setCommessaSearch('');
    setCommessaResults([]);
  };

  const onRimuoviCommessa = () => {
    setCommessaId('');
    setCommessaSelezionata(null);
    setSedeId('');
    setCodiceIvaDefaultId('');
    setAcquirenteId('');
    setOffertaCollegata(null);
  };

  const resetMiniNew = () => {
    setMnTipoSoggetto('PRIVATO');
    setMnRagioneSociale('');
    setMnNome('');
    setMnCognome('');
    setMnEmail('');
    setMnTelefono('');
  };

  const creaMiniNominativo = async () => {
    if (mnTipoSoggetto === 'AZIENDA' && !mnRagioneSociale.trim()) {
      showToast('Ragione sociale obbligatoria', 'error');
      return;
    }
    if (mnTipoSoggetto === 'PRIVATO' && (!mnNome.trim() || !mnCognome.trim())) {
      showToast('Nome e cognome obbligatori', 'error');
      return;
    }
    if (!societaId) { showToast('Società non definita', 'error'); return; }

    setMnSaving(true);
    try {
      // @api POST /api/nominativi {"societaId":"number","tipoSoggetto":"AZIENDA|PRIVATO","ragioneSociale":"string?","nome":"string?","cognome":"string?","emailPrincipale":"string?","telefono":"string?"} -> {"id":"number","codice":"string","tipoSoggetto":"string"}
      const r = await apiFetch<{ id: number; codice: string }>(
        '/api/nominativi',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: parseInt(String(societaId), 10),
            tipoSoggetto: mnTipoSoggetto,
            ragioneSociale: mnTipoSoggetto === 'AZIENDA' ? mnRagioneSociale.trim() : undefined,
            nome: mnTipoSoggetto === 'PRIVATO' ? mnNome.trim() : undefined,
            cognome: mnTipoSoggetto === 'PRIVATO' ? mnCognome.trim() : undefined,
            emailPrincipale: mnEmail.trim() || undefined,
            telefono: mnTelefono.trim() || undefined,
          }),
        }
      );
      const nuovoId = r.id;
      if (!nuovoId) throw new Error('ID non ricevuto');

      // Abilita come CLIENTE con categoria di default
      const categoria = mnTipoSoggetto === 'AZIENDA' ? 'AZIENDA_B2B' : 'PRIVATO_B2C';
      // @api POST /api/nominativi/:id/abilita-cliente {"categoriaCliente":"AZIENDA_B2B|PRIVATO_B2C"} -> {"success":true}
      await apiFetch(`/api/nominativi/${nuovoId}/abilita-cliente`, {
        method: 'POST',
        body: JSON.stringify({ categoriaCliente: categoria }),
      });

      const nuovo: NominativoOption = {
        id: nuovoId,
        codice: r.codice,
        ragioneSociale: mnTipoSoggetto === 'AZIENDA' ? mnRagioneSociale.trim() : null,
        nome: mnTipoSoggetto === 'PRIVATO' ? mnNome.trim() : null,
        cognome: mnTipoSoggetto === 'PRIVATO' ? mnCognome.trim() : null,
        tipoSoggetto: mnTipoSoggetto,
        isCliente: true,
      };

      onSelezionaNominativo(nuovo);
      setMiniNewOpen(false);
      resetMiniNew();
      showToast('Nominativo creato e selezionato', 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nella creazione', 'error');
    } finally {
      setMnSaving(false);
    }
  };

  const validaESalva = async () => {
    if (!tipo) return;
    if (!flussoCommerciale) {
      showToast('Flusso commerciale non selezionato', 'error');
      return;
    }
    if (!societaId) {
      showToast('Seleziona una società', 'error');
      return;
    }
    if (!sedeId) { showToast('Sede obbligatoria', 'error'); return; }
    if (!oggetto.trim()) { showToast('Oggetto obbligatorio', 'error'); return; }
    if (!dataEmissione) { showToast('Data emissione obbligatoria', 'error'); return; }
    if (!codiceIvaDefaultId) { showToast('Codice IVA obbligatorio', 'error'); return; }
    if (tipo === 'SEMPLICE') {
      if (usaCommessaSemplice) {
        if (!commessaId) { showToast('Commessa obbligatoria', 'error'); return; }
      } else {
        if (!nominativoId) { showToast('Cliente obbligatorio', 'error'); return; }
      }
    }
    if (tipo === 'CONFIGURATO_COMMESSA') {
      if (!commessaId) { showToast('Commessa obbligatoria', 'error'); return; }
      if (!commessaSelezionata) { showToast('Dati commessa non caricati', 'error'); return; }
      if (!commessaSelezionata.codiceIvaDefaultId) {
        showToast('La commessa non ha un codice IVA. Impostalo prima di procedere.', 'error');
        return;
      }
    }

    setSaving(true);
    try {
      // @api POST /api/preventivi {"societaId":"number","sedeId":"number","tipo":"string","flussoCommerciale":"VENDITA|APPALTO_CONTRATTO","oggetto":"string","dataEmissione":"string","dataValidita":"string?","codiceIvaDefaultId":"number","nominativoId":"number?","acquirenteId":"number?","commessaId":"number?"} -> {"success":true,"data":{"id":"number","numero":"string"}}
      const res = await apiFetch<{ success: boolean; data: { id: number; numero: string } }>(
        '/api/preventivi',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: parseInt(societaId, 10),
            sedeId: parseInt(sedeId, 10),
            tipo,
            flussoCommerciale,
            oggetto: oggetto.trim(),
            dataEmissione,
            dataValidita: dataValidita || undefined,
            codiceIvaDefaultId: parseInt(codiceIvaDefaultId, 10),
            nominativoId: (tipo === 'SEMPLICE' && usaCommessaSemplice)
              ? undefined
              : (nominativoId ? parseInt(nominativoId, 10) : undefined),
            acquirenteId: acquirenteId ? parseInt(acquirenteId, 10) : undefined,
            commessaId: (
              (tipo === 'CONFIGURATO_COMMESSA' && commessaId) ||
              (tipo === 'SEMPLICE' && usaCommessaSemplice && commessaId)
            ) ? parseInt(commessaId, 10) : undefined,
          }),
        }
      );

      showToast(`Preventivo ${res.data.numero} creato`, 'success');
      onCreato(res.data.id);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nella creazione', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="pre-modal">
      <div className="pre-modal__box">
        <div className="pre-modal__header">
          <h2 className="pre-modal__title">Nuovo preventivo</h2>
          <button className="pre-modal__close" onClick={richiediChiusura}><XIcon /></button>
        </div>

        <div className="pre-modal__body">
          {/* Indicatore step: 3 step se APPALTO_CONTRATTO o non scelto, 2 se VENDITA */}
          <div className="bop-wizard-preventivo__indicatore" role="list" aria-label="Avanzamento creazione preventivo">
            <span
              role="listitem"
              className={`bop-wizard-preventivo__step ${step === 1 ? 'bop-wizard-preventivo__step--attivo' : 'bop-wizard-preventivo__step--completato'}`}
            >
              1. Flusso
            </span>
            {flussoCommerciale !== 'VENDITA' && (
              <span
                role="listitem"
                className={`bop-wizard-preventivo__step ${step === 2 ? 'bop-wizard-preventivo__step--attivo' : step > 2 ? 'bop-wizard-preventivo__step--completato' : 'bop-wizard-preventivo__step--inattivo'}`}
              >
                2. Tipo
              </span>
            )}
            <span
              role="listitem"
              className={`bop-wizard-preventivo__step ${step === 3 ? 'bop-wizard-preventivo__step--attivo' : 'bop-wizard-preventivo__step--inattivo'}`}
            >
              {flussoCommerciale === 'VENDITA' ? '2' : '3'}. Dati
            </span>
          </div>

          {step === 1 && (
            <div className="bop-wizard-preventivo__step-flusso">
              <h2 className="bop-wizard-preventivo__titolo-step">
                Che tipo di vendita stiamo facendo?
              </h2>
              <p className="bop-wizard-preventivo__sottotitolo-step">
                Scegli il flusso commerciale del preventivo. La scelta è definitiva e
                determinerà come gestiremo ordine, fatturazione e logistica.
              </p>
              <div className="bop-wizard-preventivo__griglia-card">
                <button
                  type="button"
                  className={`bop-wizard-preventivo__card-flusso bop-wizard-preventivo__card-flusso--vendita ${flussoCommerciale === 'VENDITA' ? 'bop-wizard-preventivo__card-flusso--selezionata' : ''}`}
                  onClick={() => selezionaFlusso('VENDITA')}
                >
                  <span className="bop-wizard-preventivo__icona-card" aria-hidden="true">📦</span>
                  <span className="bop-wizard-preventivo__titolo-card">Vendita</span>
                  <span className="bop-wizard-preventivo__descrizione-card">
                    Vendita di articoli a magazzino o di servizi. Genera DDT di vendita e
                    fattura immediata. Il cliente acquista, noi consegniamo.
                  </span>
                </button>
                <button
                  type="button"
                  className={`bop-wizard-preventivo__card-flusso bop-wizard-preventivo__card-flusso--appalto ${flussoCommerciale === 'APPALTO_CONTRATTO' ? 'bop-wizard-preventivo__card-flusso--selezionata' : ''}`}
                  onClick={() => selezionaFlusso('APPALTO_CONTRATTO')}
                >
                  <span className="bop-wizard-preventivo__icona-card" aria-hidden="true">🏗️</span>
                  <span className="bop-wizard-preventivo__titolo-card">Appalto / Contratto</span>
                  <span className="bop-wizard-preventivo__descrizione-card">
                    Lavorazione di commessa con SAL e contratti. Gestiamo distinte,
                    lavorazioni, DDT di spostamento. Il cliente firma un capitolato.
                  </span>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <>
              <div className="bop-text-muted">Seleziona il tipo di preventivo da creare.</div>
              <div className="pre-tipo-grid">
                <button
                  type="button"
                  className={`pre-tipo-card ${tipo === 'SEMPLICE' ? 'pre-tipo-card--selected' : ''}`}
                  onClick={() => selezionaTipo('SEMPLICE')}
                >
                  <span className="pre-tipo-card__icon">🟦</span>
                  <span className="pre-tipo-card__title">SEMPLICE</span>
                  <span className="pre-tipo-card__desc">
                    Preventivo standard, non legato a una commessa o capitolato.
                    Diventerà un ordine con possibile creazione commessa al momento
                    della conversione.
                  </span>
                </button>
                <button
                  type="button"
                  className={`pre-tipo-card ${tipo === 'CONFIGURATO_COMMESSA' ? 'pre-tipo-card--selected' : ''}`}
                  onClick={() => selezionaTipo('CONFIGURATO_COMMESSA')}
                >
                  <span className="pre-tipo-card__icon">🟨</span>
                  {/* Label UI del tipo preventivo 'CONFIGURATO_COMMESSA' (valore enum DB). */}
                  <span className="pre-tipo-card__title">Capitolato commessa</span>
                  <span className="pre-tipo-card__desc">
                    Legato a una commessa esistente (con o senza capitolato).
                    Alla conversione verrà creato uno o due ordini secondo la
                    modalità di fatturazione.
                  </span>
                </button>
              </div>
            </>
          )}

          {step === 3 && tipo === 'SEMPLICE' && (
            <div className="pre-form-grid">
              {/* Scelta: commessa esistente o cliente diretto — primo campo della modale */}
              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">Aggancio preventivo</label>
                <div className="pre-aggancio-toggle">
                  <label className="pre-aggancio-toggle__opt">
                    <input
                      type="radio"
                      checked={!usaCommessaSemplice}
                      onChange={() => {
                        setUsaCommessaSemplice(false);
                        setCommessaId('');
                        setCommessaSelezionata(null);
                        setSedeId('');
                      }}
                    />
                    <span>Cliente diretto (nessuna commessa)</span>
                  </label>
                  <label className="pre-aggancio-toggle__opt">
                    <input
                      type="radio"
                      checked={usaCommessaSemplice}
                      onChange={() => {
                        setUsaCommessaSemplice(true);
                        setNominativoId('');
                        setNominativoSelezionato(null);
                        setSedeId('');
                      }}
                    />
                    <span>Aggancia a commessa esistente</span>
                  </label>
                </div>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Sede *</label>
                {usaCommessaSemplice ? (
                  <>
                    <input
                      className="bop-input"
                      type="text"
                      value={commessaSelezionata?.sedeNome || ''}
                      disabled
                      placeholder="Ereditata dalla commessa"
                    />
                    <small className="bop-text-muted">Ereditata dalla commessa</small>
                  </>
                ) : (
                  <select className="bop-select" value={sedeId} onChange={e => setSedeId(e.target.value)}>
                    <option value="">— Seleziona sede —</option>
                    {sedi.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                )}
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Codice IVA default *</label>
                <select className="bop-select" value={codiceIvaDefaultId} onChange={e => setCodiceIvaDefaultId(e.target.value)}>
                  <option value="">— Seleziona IVA —</option>
                  {codiciIva.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.codice} — {c.descrizione} ({formatNumber(parseFloat(c.aliquota))}%)
                    </option>
                  ))}
                </select>
              </div>
              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">Oggetto *</label>
                <input
                  className="bop-input"
                  value={oggetto}
                  onChange={e => {
                    setOggetto(e.target.value);
                    setOggettoModificato(e.target.value.trim() !== '');
                  }}
                  placeholder="Descrizione breve del preventivo"
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data emissione *</label>
                <input type="date" className="bop-input" value={dataEmissione} onChange={e => setDataEmissione(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data validità</label>
                <input type="date" className="bop-input" value={dataValidita} onChange={e => setDataValidita(e.target.value)} />
              </div>

              {usaCommessaSemplice ? (
                <div className="bop-form-group pre-form-grid--full">
                  <label className="bop-label">Commessa *</label>
                  {commessaSelezionata ? (
                    <div className="pre-cliente-selected">
                      <div className="pre-cliente-selected__info">
                        <strong>{commessaSelezionata.numero}</strong>
                        <span className="pre-cliente-selected__code">
                          {commessaSelezionata.descrizione} · {commessaSelezionata.clienteRagioneSociale}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="bop-btn bop-btn-icon"
                        onClick={() => { setCommessaId(''); setCommessaSelezionata(null); }}
                        title="Rimuovi selezione"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="pre-cliente-search">
                      <input
                        className="bop-input"
                        type="text"
                        placeholder="Cerca commessa per numero, descrizione o cliente..."
                        value={commessaSearch}
                        onChange={e => {
                          setCommessaSearch(e.target.value);
                          setCommessaSearchOpen(true);
                        }}
                        onFocus={() => setCommessaSearchOpen(true)}
                      />
                      {commessaSearchOpen && (
                        <div className="pre-cliente-search__dropdown" ref={dropdownCommessaRef}>
                          {commessaSearching ? (
                            <div className="pre-cliente-search__hint">Ricerca in corso...</div>
                          ) : commessaResults.length === 0 ? (
                            <div className="pre-cliente-search__empty">
                              <div>
                                {commessaSearch.trim().length < 2
                                  ? 'Digita almeno 2 caratteri o attendi per vedere tutte le commesse...'
                                  : 'Nessuna commessa trovata.'}
                              </div>
                            </div>
                          ) : (
                            commessaResults.map(c => (
                              <button
                                key={c.id}
                                type="button"
                                className="pre-cliente-search__item"
                                onClick={() => {
                                  setCommessaId(String(c.id));
                                  setCommessaSelezionata(c);
                                  setCommessaSearchOpen(false);
                                }}
                              >
                                <strong>{c.numero}</strong>
                                <span>{c.descrizione}</span>
                                <span className="bop-text-muted">· {c.clienteRagioneSociale}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <small className="bop-text-muted">
                    Il cliente del preventivo sarà ereditato dalla commessa. Il capitolato della commessa <strong>non</strong> viene considerato (il preventivo SEMPLICE non gestisce capitolato).
                  </small>
                </div>
              ) : (
                <div className="bop-form-group pre-form-grid--full">
                  <label className="bop-label">Cliente *</label>
                  {nominativoSelezionato ? (
                    <div className="pre-cliente-selected">
                      <div className="pre-cliente-selected__info">
                        <strong>{nominativoLabel(nominativoSelezionato)}</strong>
                        <span className="pre-cliente-selected__code">{nominativoSelezionato.codice}</span>
                      </div>
                      <button
                        type="button"
                        className="bop-btn bop-btn-icon"
                        onClick={onRimuoviNominativo}
                        title="Rimuovi selezione"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="pre-cliente-search">
                      <input
                        className="bop-input"
                        type="text"
                        placeholder="Cerca cliente per ragione sociale, nome, cognome o codice..."
                        value={clienteSearch}
                        onChange={e => {
                          setClienteSearch(e.target.value);
                          setClienteSearchOpen(true);
                        }}
                        onFocus={() => setClienteSearchOpen(true)}
                      />
                      {clienteSearchOpen && (
                        <div className="pre-cliente-search__dropdown" ref={dropdownClienteRef}>
                          {clienteSearch.trim().length < 2 ? (
                            <div className="pre-cliente-search__hint">Digita almeno 2 caratteri...</div>
                          ) : clienteSearching ? (
                            <div className="pre-cliente-search__hint">Ricerca in corso...</div>
                          ) : clienteResults.length === 0 ? (
                            <div className="pre-cliente-search__empty">
                              <div>Nessun cliente trovato.</div>
                              <button
                                type="button"
                                className="bop-btn bop-btn-primary"
                                onClick={() => setMiniNewOpen(true)}
                              >
                                + Crea nuovo nominativo
                              </button>
                            </div>
                          ) : (
                            <>
                              {clienteResults.map(n => (
                                <button
                                  key={n.id}
                                  type="button"
                                  className="pre-cliente-search__item"
                                  onClick={() => onSelezionaNominativo(n)}
                                >
                                  <div className="pre-cliente-search__item-name">
                                    {nominativoLabel(n)}
                                  </div>
                                  <div className="pre-cliente-search__item-meta">
                                    <span>{n.codice}</span>
                                    {n.tipoSoggetto && <span className="pre-cliente-search__item-tipo">{n.tipoSoggetto}</span>}
                                  </div>
                                </button>
                              ))}
                              <div className="pre-cliente-search__footer">
                                <button
                                  type="button"
                                  className="bop-btn bop-btn-secondary"
                                  onClick={() => setMiniNewOpen(true)}
                                >
                                  + Crea nuovo nominativo
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 3 && tipo === 'CONFIGURATO_COMMESSA' && (
            <div className="pre-form-grid">
              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">Modalità commessa</label>
                <div className="pre-toggle-row">
                  <button
                    type="button"
                    className={`pre-toggle-btn ${modoCommessa === 'esistente' ? 'pre-toggle-btn--active' : ''}`}
                    onClick={() => setModoCommessa('esistente')}
                  >
                    Da commessa esistente
                  </button>
                  <button
                    type="button"
                    className="pre-toggle-btn"
                    disabled
                    title="Disponibile prossimamente"
                  >
                    Nuova commessa
                  </button>
                </div>
              </div>

              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">Commessa *</label>
                {commessaSelezionata ? (
                  <div className="pre-commessa-selected">
                    <div className="pre-commessa-selected__info">
                      <div className="pre-commessa-selected__row1">
                        <strong>{commessaSelezionata.numero}</strong>
                        <span>— {commessaSelezionata.descrizione}</span>
                        {commessaSelezionata.offertaCapitolatoId && (
                          <span className="pre-commessa-badge pre-commessa-badge--capitolato">Capitolato</span>
                        )}
                      </div>
                      <div className="pre-commessa-selected__row2">
                        {commessaSelezionata.clienteRagioneSociale}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="bop-btn bop-btn-icon"
                      onClick={onRimuoviCommessa}
                      title="Rimuovi selezione"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="pre-commessa-search">
                    <input
                      className="bop-input"
                      type="text"
                      placeholder="Cerca commessa per numero, descrizione o cliente..."
                      value={commessaSearch}
                      onChange={e => {
                        setCommessaSearch(e.target.value);
                        setCommessaSearchOpen(true);
                      }}
                      onFocus={() => setCommessaSearchOpen(true)}
                    />
                    {commessaSearchOpen && (
                      <div className="pre-commessa-search__dropdown">
                        {commessaSearching ? (
                          <div className="pre-commessa-search__hint">Ricerca in corso...</div>
                        ) : commessaResults.length === 0 ? (
                          <div className="pre-commessa-search__hint">
                            {commessaSearch.trim().length < 2
                              ? 'Digita almeno 2 caratteri o attendi per vedere tutte le commesse...'
                              : 'Nessuna commessa trovata'}
                          </div>
                        ) : (
                          commessaResults.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              className="pre-commessa-search__item"
                              onClick={() => onSelezionaCommessa(c)}
                            >
                              <div className="pre-commessa-search__item-row1">
                                <span className="pre-commessa-search__numero">{c.numero}</span>
                                <span className="pre-commessa-search__desc">— {c.descrizione}</span>
                                {c.offertaCapitolatoId && (
                                  <span className="pre-commessa-badge pre-commessa-badge--capitolato">Capitolato</span>
                                )}
                              </div>
                              <div className="pre-commessa-search__item-row2">
                                {c.clienteRagioneSociale}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {commessaSelezionata && !commessaSelezionata.codiceIvaDefaultId && (
                <div className="pre-form-grid--full">
                  <div className="pre-banner-warning">
                    <strong>⚠ Codice IVA mancante</strong>
                    <div>
                      Questa commessa non ha un codice IVA default. È necessario impostarlo prima di creare il preventivo.
                      {' '}
                      <a
                        href={`/commerciale/commesse/${commessaSelezionata.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="prev-link-inline"
                      >
                        Apri la commessa
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {offertaCollegata && (
                <div className="pre-form-grid--full">
                  <div className="pre-banner-capitolato">
                    <div className="pre-banner-capitolato__title">
                      Capitolato collegato: {offertaCollegata.codice}
                    </div>
                    <div>
                      Ricarico impresa: {offertaCollegata.ricarico != null ? `${offertaCollegata.ricarico}%` : '—'}
                      {' • '}
                      Fatturazione: {offertaCollegata.tipoFatturazione || '—'}
                    </div>
                  </div>
                </div>
              )}

              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">
                  Acquirente (opzionale)
                </label>
                <select
                  className="bop-select"
                  value={acquirenteId}
                  onChange={e => setAcquirenteId(e.target.value)}
                  disabled={acquirenti.length === 0}
                >
                  <option value="">— Nessun acquirente specifico —</option>
                  {acquirenti.map(a => {
                    const persona = `${a.nome || ''} ${a.cognome || ''}`.trim();
                    return (
                      <option key={a.id} value={a.id}>
                        {a.unitaCodice} — {persona || a.unitaDescrizione || '(senza nome)'}
                      </option>
                    );
                  })}
                </select>
                {offertaCollegata?.tipoFatturazione === 'CLIENTE_PAGA_DIRETTAMENTE' && (
                  <small className="bop-text-muted">
                    {acquirenteId
                      ? 'Il preventivo userà la formula extra-capitolato (split impresa/acquirente in conversione).'
                      : 'Senza acquirente il preventivo è "fuori capitolato": andrà direttamente all\'impresa, senza split.'}
                  </small>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Sede</label>
                <input
                  className="bop-input"
                  type="text"
                  value={commessaSelezionata?.sedeNome || ''}
                  disabled
                  placeholder={commessaSelezionata ? '—' : 'Seleziona prima la commessa'}
                />
                <small className="bop-text-muted">Ereditata dalla commessa</small>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Codice IVA default</label>
                <input
                  className="bop-input"
                  type="text"
                  value={
                    commessaSelezionata?.codiceIvaDefault
                      ? `${commessaSelezionata.codiceIvaDefault.codice} — ${commessaSelezionata.codiceIvaDefault.descrizione} (${formatNumber(parseFloat(commessaSelezionata.codiceIvaDefault.aliquota))}%)`
                      : ''
                  }
                  disabled
                  placeholder={commessaSelezionata ? '—' : 'Seleziona prima la commessa'}
                />
                <small className="bop-text-muted">Ereditato dalla commessa</small>
              </div>

              <div className="bop-form-group pre-form-grid--full">
                <label className="bop-label">Oggetto *</label>
                <input
                  className="bop-input"
                  value={oggetto}
                  onChange={e => {
                    setOggetto(e.target.value);
                    setOggettoModificato(e.target.value.trim() !== '');
                  }}
                  placeholder="Descrizione breve del preventivo"
                />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Data emissione *</label>
                <input type="date" className="bop-input" value={dataEmissione} onChange={e => setDataEmissione(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data validità</label>
                <input type="date" className="bop-input" value={dataValidita} onChange={e => setDataValidita(e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div className="pre-modal__footer">
          {step === 1 && (
            <>
              <button className="bop-btn bop-btn-secondary" onClick={richiediChiusura}>Annulla</button>
              {/* In step 1 le card auto-avanzano: nessun pulsante "Avanti" */}
            </>
          )}
          {step === 2 && (
            <>
              <button className="bop-btn bop-btn-secondary" onClick={indietroDaStep2}>
                Indietro
              </button>
              <button
                className="bop-btn bop-btn-primary"
                disabled={!tipo}
                onClick={() => setStep(3)}
              >
                Avanti
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <button className="bop-btn bop-btn-secondary" onClick={indietroDaStep3} disabled={saving}>
                Indietro
              </button>
              <button
                className="bop-btn bop-btn-primary"
                onClick={validaESalva}
                disabled={
                  saving ||
                  (tipo === 'CONFIGURATO_COMMESSA' && (
                    !commessaSelezionata ||
                    !commessaSelezionata.codiceIvaDefaultId
                  ))
                }
              >
                {saving ? 'Creazione...' : 'Crea e apri'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>

    {miniNewOpen && (
      <div className="mini-modal-overlay" onClick={() => !mnSaving && setMiniNewOpen(false)}>
        <div className="mini-modal" onClick={e => e.stopPropagation()}>
          <div className="mini-modal__header">
            <h3 className="mini-modal__title">Nuovo nominativo</h3>
            <button
              type="button"
              className="bop-btn bop-btn-icon"
              onClick={() => !mnSaving && setMiniNewOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="mini-modal__body">
            <div className="bop-form-group">
              <label className="bop-label">Tipo soggetto *</label>
              <div className="pre-toggle-row">
                <button
                  type="button"
                  className={`pre-toggle-btn ${mnTipoSoggetto === 'PRIVATO' ? 'pre-toggle-btn--active' : ''}`}
                  onClick={() => setMnTipoSoggetto('PRIVATO')}
                >
                  Privato
                </button>
                <button
                  type="button"
                  className={`pre-toggle-btn ${mnTipoSoggetto === 'AZIENDA' ? 'pre-toggle-btn--active' : ''}`}
                  onClick={() => setMnTipoSoggetto('AZIENDA')}
                >
                  Azienda
                </button>
              </div>
            </div>

            {mnTipoSoggetto === 'AZIENDA' ? (
              <div className="bop-form-group">
                <label className="bop-label">Ragione sociale *</label>
                <input
                  className="bop-input"
                  value={mnRagioneSociale}
                  onChange={e => setMnRagioneSociale(e.target.value)}
                  autoFocus
                />
              </div>
            ) : (
              <div className="pre-form-grid">
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input
                    className="bop-input"
                    value={mnNome}
                    onChange={e => setMnNome(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome *</label>
                  <input
                    className="bop-input"
                    value={mnCognome}
                    onChange={e => setMnCognome(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="bop-form-group">
              <label className="bop-label">Email</label>
              <input
                className="bop-input"
                type="email"
                value={mnEmail}
                onChange={e => setMnEmail(e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Telefono</label>
              <input
                className="bop-input"
                type="tel"
                value={mnTelefono}
                onChange={e => setMnTelefono(e.target.value)}
              />
            </div>
          </div>
          <div className="mini-modal__footer">
            <button
              type="button"
              className="bop-btn bop-btn-secondary"
              onClick={() => setMiniNewOpen(false)}
              disabled={mnSaving}
            >
              Annulla
            </button>
            <button
              type="button"
              className="bop-btn bop-btn-primary"
              onClick={creaMiniNominativo}
              disabled={mnSaving}
            >
              {mnSaving ? 'Creazione...' : 'Crea e seleziona'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function PreventiviPage() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can('commerciale.preventivi', 'edit');
  const canDelete = can('commerciale.preventivi', 'delete');

  const [items, setItems] = useState<PreventivoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<'' | TipoPreventivo>('');
  const [filtroFlusso, setFiltroFlusso] = useState<'' | FlussoCommerciale>('');
  const [filtroStato, setFiltroStato] = useState<'' | StatoPreventivo>('');
  const [mostraSuperati, setMostraSuperati] = useState(false);
  const [q, setQ] = useState('');
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const [showNuovo, setShowNuovo] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PreventivoItem | null>(null);
  // Versione precedente da decidere quando si elimina una "nuova versione"
  type OrigineDaRipristinare = { id: number; numero: string; stato: string; statoRipristino: string };
  const [sceltaOrigine, setSceltaOrigine] = useState<OrigineDaRipristinare | null>(null);

  const societaIdEff = societaId || societaIdLocale;
  const columns = useMemo(() => buildPreventiviColumns(), []);

  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (filtroTipo) params.set('tipo', filtroTipo);
      if (filtroFlusso) params.set('flussoCommerciale', filtroFlusso);
      if (filtroStato) params.set('stato', filtroStato);
      if (q.trim()) params.set('q', q.trim());
      if (mostraSuperati) params.set('includiSuperati', 'true');

      // @api GET /api/preventivi?societaId={id}&tipo={tipo}&flussoCommerciale={flusso}&stato={stato}&q={q}&includiSuperati={bool} -> {"success":true,"data":[{"id":"number","numero":"string","versione":"number","tipo":"string","flussoCommerciale":"string","stato":"string","oggetto":"string","dataEmissione":"string","clienteRagioneSociale":"string","totaleCliente":"string","commessa":{"id":"number","numero":"string"},"acquirente":{"id":"number","nome":"string","cognome":"string","unitaCodice":"string"}}]}
      const r = await apiFetch<{ success: boolean; data: PreventivoItem[] }>(
        `/api/preventivi?${params.toString()}`
      );
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel caricamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, filtroTipo, filtroFlusso, filtroStato, q, mostraSuperati]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Se il preventivo è la nuova versione di un altro, il backend chiede (409
  // SCELTA_RIPRISTINO_ORIGINE) cosa fare della versione precedente: la domanda
  // viene posta all'utente e la DELETE ripetuta con la sua scelta.
  const handleDelete = async (ripristinaOrigine?: boolean) => {
    if (!confirmDelete) return;
    try {
      const qs = ripristinaOrigine === undefined ? '' : `?ripristinaOrigine=${ripristinaOrigine ? 1 : 0}`;
      // @api DELETE /api/preventivi/:id?ripristinaOrigine=0|1 -> {"success":true,"data":{"origineRipristinata":{"id":"number","numero":"string","stato":"string"}|null}}
      const res = await apiFetch<{ success: boolean; data?: { origineRipristinata: { numero: string; stato: string } | null } }>(
        `/api/preventivi/${confirmDelete.id}${qs}`,
        { method: 'DELETE' }
      );
      const rip = res.data?.origineRipristinata;
      showToast(rip ? `Preventivo eliminato. ${rip.numero} riportato a ${(STATO_LABEL as Record<string, string>)[rip.stato] ?? rip.stato}` : 'Preventivo eliminato', 'success');
      setConfirmDelete(null);
      setSceltaOrigine(null);
      caricaLista();
    } catch (err: unknown) {
      const payload = (err as { payload?: { code?: string; origine?: OrigineDaRipristinare } }).payload;
      if (payload?.code === 'SCELTA_RIPRISTINO_ORIGINE' && payload.origine) {
        setSceltaOrigine(payload.origine);
        return;
      }
      showToast(err instanceof Error ? err.message : 'Errore nell\'eliminazione', 'error');
    }
  };

  return (
    <Layout>
      <div className="pre-page">
        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Preventivi</h1>
          {canEdit && pronta && (
            <button className="bop-btn bop-btn-primary" onClick={() => {
              if (!societaIdEff) {
                showToast('Seleziona una società prima di creare un preventivo', 'warning');
                return;
              }
              setShowNuovo(true);
            }}>
              <PlusIcon /> Nuovo preventivo
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar pre-filters-row">
            {!isDisabled && (
              <div className="bop-form-group">
                <label className="bop-label">Società</label>
                <select
                  className="bop-select"
                  value={societaIdLocale}
                  onChange={e => setSocietaIdLocale(e.target.value)}
                >
                  {isSuperadmin && <option value="">Tutte</option>}
                  {listaSocieta.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">Cerca</label>
              <input
                className="bop-input pre-search-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Numero, oggetto, cliente..."
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Tipo</label>
              <select
                className="bop-select"
                value={filtroTipo}
                onChange={e => setFiltroTipo(e.target.value as '' | TipoPreventivo)}
              >
                <option value="">Tutti</option>
                <option value="SEMPLICE">Semplice</option>
                {/* value = valore enum TipoPreventivo nel DB; label = testo UI */}
                <option value="CONFIGURATO_COMMESSA">Capitolato commessa</option>
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Flusso commerciale</label>
              <select
                className="bop-select"
                value={filtroFlusso}
                onChange={e => setFiltroFlusso(e.target.value as '' | FlussoCommerciale)}
              >
                <option value="">Tutti</option>
                <option value="VENDITA">📦 Vendita</option>
                <option value="APPALTO_CONTRATTO">🏗️ Appalto/Contratto</option>
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Stato</label>
              <select
                className="bop-select"
                value={filtroStato}
                onChange={e => setFiltroStato(e.target.value as '' | StatoPreventivo)}
              >
                <option value="">Tutti</option>
                <option value="BOZZA">Bozza</option>
                <option value="INVIATO">Inviato</option>
                <option value="ACCETTATO">Accettato</option>
                <option value="RIFIUTATO">Rifiutato</option>
                <option value="SCADUTO">Scaduto</option>
                <option value="SUPERATO">Superato</option>
                <option value="CONVERTITO">Convertito</option>
                <option value="ANNULLATO">Annullato</option>
                <option value="ANNULLATO_DOPO_CONFERMA">Annullato dopo conferma</option>
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">&nbsp;</label>
              <label className="pre-toggle-superati">
                <input
                  type="checkbox"
                  checked={mostraSuperati}
                  onChange={e => setMostraSuperati(e.target.checked)}
                />
                <span>Mostra superati</span>
              </label>
            </div>
          </div>

        {/* Tabella Excel-like */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare i preventivi</div>
          </div>
        ) : (
          <BopExcelTable<PreventivoItem>
            data={items}
            loading={loading}
            rowKey={(p) => p.id}
            onRowClick={(p) => navigate(`/commerciale/attivo/preventivi/${p.id}`)}
            initialSort={{ key: 'dataEmissione', dir: 'desc' }}
            emptyState={
              <div className="pre-empty">
                <svg className="pre-empty__icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <h3 className="pre-empty__title">Nessun preventivo trovato</h3>
                <p className="pre-empty__text">
                  {canEdit ? 'Clicca "Nuovo preventivo" per crearne uno.' : 'Non sono presenti preventivi per i filtri selezionati.'}
                </p>
              </div>
            }
            columns={columns}
            rowActions={(p) => (
              // Eliminabile in ogni stato tranne CONVERTITO (ha ordini in coda: il
              // backend risponde 409). Eliminando l'ordine il preventivo torna ACCETTATO.
              canDelete && p.stato !== 'CONVERTITO' ? (
                <button
                  className="bop-table__action-btn bop-table__action-btn--danger"
                  title="Elimina"
                  aria-label="Elimina preventivo"
                  onClick={() => setConfirmDelete(p)}
                >
                  <TrashIcon />
                </button>
              ) : null
            )}
          />
        )}

        {/* Modale Nuovo */}
        {showNuovo && (
          <ModalNuovoPreventivo
            societaId={societaIdEff}
            onClose={() => setShowNuovo(false)}
            onCreato={(id) => {
              setShowNuovo(false);
              navigate(`/commerciale/attivo/preventivi/${id}`);
            }}
          />
        )}

        {/* Conferma elimina */}
        {confirmDelete && (
          <div className="pre-modal">
            <div className="pre-modal__box">
              <div className="pre-modal__header">
                <h2 className="pre-modal__title">Elimina preventivo</h2>
                <button className="pre-modal__close" onClick={() => { setConfirmDelete(null); setSceltaOrigine(null); }}><XIcon /></button>
              </div>
              <div className="pre-modal__body">
                {sceltaOrigine ? (
                  <>
                    <p>
                      Il preventivo <strong>{confirmDelete.numero}</strong> è la nuova versione di{' '}
                      <strong>{sceltaOrigine.numero}</strong>, che oggi è in stato{' '}
                      <strong>{(STATO_LABEL as Record<string, string>)[sceltaOrigine.stato] ?? sceltaOrigine.stato}</strong>.
                    </p>
                    <p>
                      Cosa vuoi fare della versione precedente? Puoi riportarla a{' '}
                      <strong>{(STATO_LABEL as Record<string, string>)[sceltaOrigine.statoRipristino] ?? sceltaOrigine.statoRipristino}</strong>{' '}
                      oppure lasciarla com'è.
                    </p>
                  </>
                ) : (
                  <p>
                    Eliminare il preventivo <strong>{confirmDelete.numero}</strong> ({confirmDelete.oggetto})?
                    L'operazione non è reversibile.
                  </p>
                )}
              </div>
              <div className="pre-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => { setConfirmDelete(null); setSceltaOrigine(null); }}>Annulla</button>
                {sceltaOrigine ? (
                  <>
                    <button className="bop-btn bop-btn-secondary" onClick={() => handleDelete(false)}>Elimina e lascia com'è</button>
                    <button className="bop-btn bop-btn-danger" onClick={() => handleDelete(true)}>Elimina e ripristina {sceltaOrigine.numero}</button>
                  </>
                ) : (
                  <button className="bop-btn bop-btn-danger" onClick={() => handleDelete()}>Elimina</button>
                )}
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
